import React, { useEffect, useMemo, useState } from 'react';
import {
  BookOpenCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Filter,
  Search,
  ShieldCheck,
} from 'lucide-react';

import {
  loadEquipmentLibraryIndex,
  queryEquipmentLibraryIndex,
  type EquipmentAdmissionLevel,
  type EquipmentLibraryIndex,
  type EquipmentLibraryQuery,
  type EquipmentLibraryRow,
} from '../../services/equipmentLibrary/equipmentLibraryStore';

export interface EquipmentLibraryBrowserProps {
  companyId?: string | null;
  index?: EquipmentLibraryIndex;
  pageSize?: number;
  selectionLevel?: EquipmentAdmissionLevel;
  selectedProductId?: string;
  onSelect?: (row: EquipmentLibraryRow) => void;
}

const LEVELS: ReadonlyArray<{
  id: EquipmentAdmissionLevel;
  short: string;
  label: string;
}> = [
  { id: 'library-visible', short: 'LIB', label: 'Library visible' },
  { id: 'bom-ready', short: 'BOM', label: 'BOM ready' },
  { id: 'screening-ready', short: 'SCR', label: 'Screening ready' },
  { id: 'simulation-ready', short: 'SIM', label: 'Simulation ready' },
];

type FilterState = Pick<EquipmentLibraryQuery,
  | 'search'
  | 'manufacturer'
  | 'kind'
  | 'technology'
  | 'fuel'
  | 'primeMover'
  | 'cycle'
  | 'frequencyHz'
  | 'region'
  | 'application'
  | 'lifecycleStatus'
  | 'evidenceTier'
  | 'admissionLevel'
  | 'includeResearchOnly'
>;

const INITIAL_FILTERS: FilterState = {
  search: '',
  includeResearchOnly: true,
};

function sortedUnique(values: readonly (string | undefined)[]): string[] {
  return [...new Set(values.filter((value): value is string => Boolean(value)))]
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }));
}

function admissionTone(status: string): string {
  if (status === 'admitted') return 'border-success/50 bg-success/15 text-success';
  if (status === 'comparison-only') return 'border-warning/50 bg-warning/10 text-warning';
  if (status === 'needs-data') return 'border-warning/40 bg-warning/5 text-warning';
  if (status === 'rejected' || status === 'retired') {
    return 'border-danger/40 bg-danger/10 text-danger';
  }
  return 'border-border bg-background text-text-muted';
}

function evidenceLabel(tier: string): string {
  switch (tier) {
    case 'tier-1-oem-primary':
      return 'OEM primary';
    case 'tier-2-regulatory-registry':
      return 'Registry sourced';
    case 'tier-3-government-or-lab':
      return 'Government / lab';
    case 'private-unverified':
      return 'Private · unverified';
    default:
      return tier;
  }
}

function FacetSelect({
  label,
  value,
  values,
  onChange,
}: {
  label: string;
  value: string;
  values: readonly string[];
  onChange: (value: string) => void;
}): React.JSX.Element {
  return (
    <label className="min-w-[9rem] flex-1">
      <span className="mb-1 block text-[9px] font-bold uppercase tracking-[0.14em] text-text-muted">
        {label}
      </span>
      <select
        aria-label={label}
        value={value}
        onChange={event => onChange(event.target.value)}
        className="w-full border border-border bg-background px-2 py-1.5 text-[10px] text-text-secondary"
      >
        <option value="">All</option>
        {values.map(item => <option key={item} value={item}>{item}</option>)}
      </select>
    </label>
  );
}

function EvidenceSpine({ row }: { row: EquipmentLibraryRow }): React.JSX.Element {
  return (
    <div
      className="grid w-[3.2rem] shrink-0 grid-rows-4 gap-1 border-r border-border pr-3"
      aria-label="Admission evidence spine"
    >
      {LEVELS.map(level => {
        const status = row.admissionSpine[level.id];
        return (
          <span
            key={level.id}
            title={`${level.label}: ${status}`}
            className={`flex items-center justify-center border px-1 py-1 text-[8px] font-black tracking-[0.12em] ${admissionTone(status)}`}
          >
            {level.short}
          </span>
        );
      })}
    </div>
  );
}

export function EquipmentLibraryBrowser({
  companyId,
  index: suppliedIndex,
  pageSize = 25,
  selectionLevel,
  selectedProductId,
  onSelect,
}: EquipmentLibraryBrowserProps): React.JSX.Element {
  const [index, setIndex] = useState<EquipmentLibraryIndex | null>(suppliedIndex ?? null);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | undefined>();
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);

  useEffect(() => {
    if (suppliedIndex) {
      setIndex(suppliedIndex);
      setError(null);
      return;
    }
    let cancelled = false;
    void loadEquipmentLibraryIndex(companyId)
      .then(next => {
        if (!cancelled) setIndex(next);
      })
      .catch(reason => {
        if (!cancelled) setError((reason as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, suppliedIndex]);

  const facets = useMemo(() => {
    const rows = index?.rows ?? [];
    return {
      manufacturers: sortedUnique(rows.map(row => row.product.manufacturer)),
      kinds: sortedUnique(rows.map(row => row.product.kind)),
      technologies: sortedUnique(rows.flatMap(row => row.product.facets.technologies)),
      fuels: sortedUnique(rows.flatMap(row => row.product.facets.fuels)),
      primeMovers: sortedUnique(rows.flatMap(row => row.product.facets.primeMovers)),
      cycles: sortedUnique(rows.flatMap(row => row.product.facets.cycles)),
      regions: sortedUnique(rows.flatMap(row => row.product.facets.regions)),
      applications: sortedUnique(rows.flatMap(row => row.product.facets.applications)),
      statuses: sortedUnique(rows.map(row => row.product.lifecycleStatus)),
      evidenceTiers: sortedUnique(rows.map(row => row.evidenceTier)),
    };
  }, [index]);

  const page = useMemo(() => (
    index
      ? queryEquipmentLibraryIndex(index, {
        ...filters,
        pageSize,
        ...(cursor ? { cursor } : {}),
      })
      : null
  ), [cursor, filters, index, pageSize]);

  const setFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]): void => {
    setFilters(current => ({ ...current, [key]: value }));
    setCursor(undefined);
    setCursorHistory([]);
  };

  const reviewedSourceCount = useMemo(
    () => new Set((index?.rows ?? []).flatMap(row => row.sources.map(source => source.id))).size,
    [index],
  );
  const manufacturerCount = facets.manufacturers.length;
  const researchCount = index?.rows.filter(row => row.researchOnly).length ?? 0;

  if (error) {
    return (
      <div className="border border-danger/40 bg-danger/5 p-4 text-xs text-danger" role="alert">
        Equipment Library unavailable: {error}
      </div>
    );
  }
  if (!index || !page) {
    return (
      <div className="border border-border p-5 text-[10px] uppercase tracking-wider text-text-muted">
        Loading evidence-bound equipment library…
      </div>
    );
  }

  return (
    <section className="overflow-hidden border border-border bg-card" data-testid="equipment-library-browser">
      <div className="flex flex-wrap items-start gap-4 border-b border-border bg-background/50 px-4 py-3">
        <div className="min-w-[16rem] flex-1">
          <div className="flex items-center gap-2">
            <BookOpenCheck className="h-4 w-4 text-primary" aria-hidden />
            <h2 className="text-xs font-black uppercase tracking-[0.18em] text-text-primary">
              Equipment Library
            </h2>
          </div>
          <p className="mt-1 max-w-3xl text-[10px] leading-relaxed text-text-muted">
            OEM products and documented capability families. Technical facts, market evidence,
            and workflow admission remain separate; unknown values stay blank.
          </p>
        </div>
        <div className="grid grid-cols-4 gap-px border border-border bg-border text-center">
          {[
            ['Products', index.rows.length],
            ['Manufacturers', manufacturerCount],
            ['Sources', reviewedSourceCount],
            ['Research', researchCount],
          ].map(([label, value]) => (
            <div key={label} className="min-w-[5.2rem] bg-card px-2 py-1.5">
              <span className="block text-[8px] font-bold uppercase tracking-wider text-text-muted">{label}</span>
              <strong className="text-sm tabular-nums text-text-primary">{value}</strong>
            </div>
          ))}
        </div>
      </div>

      <div className="border-b border-border p-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="min-w-[15rem] flex-[2]">
            <span className="mb-1 block text-[9px] font-bold uppercase tracking-[0.14em] text-text-muted">
              Search evidence index
            </span>
            <span className="relative block">
              <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-muted" />
              <input
                aria-label="Search evidence index"
                value={filters.search ?? ''}
                onChange={event => setFilter('search', event.target.value)}
                placeholder="Manufacturer, family, technology, source…"
                className="w-full border border-border bg-background py-1.5 pl-7 pr-2 text-[10px] text-text-primary"
              />
            </span>
          </label>
          <FacetSelect
            label="Manufacturer"
            value={filters.manufacturer ?? ''}
            values={facets.manufacturers}
            onChange={value => setFilter('manufacturer', value || undefined)}
          />
          <FacetSelect
            label="Equipment class"
            value={filters.kind ?? ''}
            values={facets.kinds}
            onChange={value => setFilter('kind', value || undefined)}
          />
          <FacetSelect
            label="Technology"
            value={filters.technology ?? ''}
            values={facets.technologies}
            onChange={value => setFilter('technology', value || undefined)}
          />
          <FacetSelect
            label="Commercial status"
            value={filters.lifecycleStatus ?? ''}
            values={facets.statuses}
            onChange={value => setFilter('lifecycleStatus', value || undefined)}
          />
          <button
            type="button"
            onClick={() => setAdvancedOpen(value => !value)}
            className="inline-flex h-[29px] items-center gap-1 border border-border px-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary hover:border-primary hover:text-primary"
            aria-expanded={advancedOpen}
          >
            <Filter className="h-3 w-3" /> More facets
          </button>
        </div>

        {advancedOpen && (
          <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-border pt-3" data-testid="equipment-library-advanced-facets">
            <FacetSelect label="Fuel" value={filters.fuel ?? ''} values={facets.fuels} onChange={value => setFilter('fuel', value || undefined)} />
            <FacetSelect label="Prime mover" value={filters.primeMover ?? ''} values={facets.primeMovers} onChange={value => setFilter('primeMover', value || undefined)} />
            <FacetSelect label="Cycle" value={filters.cycle ?? ''} values={facets.cycles} onChange={value => setFilter('cycle', value || undefined)} />
            <FacetSelect label="Region" value={filters.region ?? ''} values={facets.regions} onChange={value => setFilter('region', value || undefined)} />
            <FacetSelect label="Application" value={filters.application ?? ''} values={facets.applications} onChange={value => setFilter('application', value || undefined)} />
            <FacetSelect label="Evidence tier" value={filters.evidenceTier ?? ''} values={facets.evidenceTiers} onChange={value => setFilter('evidenceTier', value || undefined)} />
            <FacetSelect label="Admission level" value={filters.admissionLevel ?? ''} values={LEVELS.map(level => level.id)} onChange={value => setFilter('admissionLevel', (value || undefined) as EquipmentAdmissionLevel | undefined)} />
            <FacetSelect label="Frequency" value={filters.frequencyHz?.toString() ?? ''} values={['50', '60']} onChange={value => setFilter('frequencyHz', value ? Number(value) as 50 | 60 : undefined)} />
            <label className="flex h-[29px] items-center gap-2 border border-border px-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary">
              <input
                type="checkbox"
                checked={filters.includeResearchOnly === true}
                onChange={event => setFilter('includeResearchOnly', event.target.checked)}
              />
              Show research-only
            </label>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-b border-border px-4 py-2 text-[9px] uppercase tracking-wider text-text-muted">
        <span>{page.total} matching records</span>
        <span>Evidence spine · LIB / BOM / SCR / SIM</span>
      </div>

      <div className="divide-y divide-border" data-testid="equipment-library-results">
        {page.rows.map(row => {
          const expanded = expandedId === row.product.id;
          const selectable = selectionLevel == null
            || row.admissionSpine[selectionLevel] === 'admitted';
          return (
            <article key={row.product.id} data-testid={`equipment-library-row-${row.product.id}`}>
              <div className="flex gap-3 px-4 py-3 hover:bg-background/40">
                <EvidenceSpine row={row} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-xs font-black text-text-primary">{row.view.displayName}</h3>
                    <span className="border border-primary/30 bg-primary/5 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-primary">
                      {row.sourceKind}
                    </span>
                    <span className={`border px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider ${
                      row.researchOnly
                        ? 'border-warning/40 bg-warning/10 text-warning'
                        : 'border-success/40 bg-success/10 text-success'
                    }`}>
                      {row.researchOnly ? 'research-only' : row.product.lifecycleStatus}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[9px] text-text-muted">
                    {row.product.kind.replaceAll('-', ' ')}
                    {' · '}
                    {row.product.domain.replaceAll('-', ' ')}
                    {row.revision?.frequencyHz.length
                      ? ` · ${row.revision.frequencyHz.join('/')} Hz`
                      : ' · frequency undocumented'}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[9px]">
                    <span><span className="text-text-muted">Evidence</span> <strong className="text-text-secondary">{evidenceLabel(row.evidenceTier)}</strong></span>
                    <span><span className="text-text-muted">Sources</span> <strong className="tabular-nums text-text-secondary">{row.sources.length}</strong></span>
                    <span><span className="text-text-muted">Facts</span> <strong className="tabular-nums text-text-secondary">{row.observations.length}</strong></span>
                    <span><span className="text-text-muted">Market rows</span> <strong className="tabular-nums text-text-secondary">{row.marketEvidence.length}</strong></span>
                  </div>
                </div>
                <div className="flex shrink-0 items-start gap-2">
                  {onSelect && (
                    <button
                      type="button"
                      onClick={() => onSelect(row)}
                      disabled={!selectable || selectedProductId === row.product.id}
                      title={!selectable && selectionLevel
                        ? `Not ${selectionLevel}`
                        : undefined}
                      className="border border-primary px-2.5 py-1.5 text-[8px] font-black uppercase tracking-wider text-primary hover:bg-primary/5 disabled:border-border disabled:text-text-muted"
                    >
                      {selectedProductId === row.product.id ? 'Selected' : 'Select'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : row.product.id)}
                    aria-expanded={expanded}
                    aria-label={`${expanded ? 'Collapse' : 'Expand'} ${row.view.displayName}`}
                    className="border border-border p-1.5 text-text-muted hover:border-primary hover:text-primary"
                  >
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              </div>
              {expanded && (
                <div className="grid gap-4 border-t border-border bg-background/40 px-4 py-3 pl-[5.4rem] md:grid-cols-2">
                  <div>
                    <h4 className="flex items-center gap-1 text-[9px] font-black uppercase tracking-[0.14em] text-text-secondary">
                      <ShieldCheck className="h-3 w-3 text-primary" /> Admission record
                    </h4>
                    <dl className="mt-2 grid grid-cols-[7rem_1fr] gap-x-2 gap-y-1 text-[9px]">
                      {LEVELS.map(level => (
                        <React.Fragment key={level.id}>
                          <dt className="text-text-muted">{level.label}</dt>
                          <dd className="font-bold text-text-secondary">{row.admissionSpine[level.id]}</dd>
                        </React.Fragment>
                      ))}
                    </dl>
                    {row.revision?.notes && (
                      <p className="mt-2 border-l-2 border-warning/50 pl-2 text-[9px] leading-relaxed text-text-muted">
                        {row.revision.notes}
                      </p>
                    )}
                  </div>
                  <div>
                    <h4 className="text-[9px] font-black uppercase tracking-[0.14em] text-text-secondary">
                      Current sources
                    </h4>
                    <ul className="mt-2 space-y-1.5">
                      {row.sources.map(source => (
                        <li key={source.id} className="text-[9px] text-text-muted">
                          {source.visibility === 'public' ? (
                            <a
                              href={source.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-start gap-1 text-primary hover:underline"
                            >
                              <span>{source.publisher} · {source.title}</span>
                              <ExternalLink className="mt-0.5 h-2.5 w-2.5 shrink-0" />
                            </a>
                          ) : `${source.publisher} · private document`}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </article>
          );
        })}
        {page.rows.length === 0 && (
          <p className="m-4 border border-dashed border-border p-4 text-[10px] text-text-muted">
            No permitted OEM records match these facets.
          </p>
        )}
      </div>

      <footer className="flex items-center justify-between border-t border-border px-4 py-2">
        <span className="text-[9px] text-text-muted">
          Snapshot {index.compiledAt.slice(0, 10)}
          {index.warnings.length > 0 ? ` · ${index.warnings.length} validation warning(s)` : ''}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous equipment page"
            disabled={cursorHistory.length === 0}
            onClick={() => {
              const previous = cursorHistory.at(-1) ?? '';
              setCursorHistory(history => history.slice(0, -1));
              setCursor(previous || undefined);
            }}
            className="border border-border p-1.5 text-text-secondary disabled:text-text-muted"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <span className="px-2 text-[9px] uppercase tracking-wider text-text-muted">
            Page {cursorHistory.length + 1}
          </span>
          <button
            type="button"
            aria-label="Next equipment page"
            disabled={!page.nextCursor}
            onClick={() => {
              if (!page.nextCursor) return;
              setCursorHistory(history => [...history, cursor ?? '']);
              setCursor(page.nextCursor ?? undefined);
            }}
            className="border border-border p-1.5 text-text-secondary disabled:text-text-muted"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </footer>
    </section>
  );
}

